/**
 * Service for fetching book/reading journal data from the backend
 */

import { API_URL } from '../config/api';

/**
 * Fetches all journals from the backend with pagination
 */
export const fetchJournals = async (page = 0, size = 12) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString()
        });

        const response = await fetch(`${API_URL}/journals?${params}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch journals: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error('Error fetching journals:', error);
        throw error;
    }
};

/**
 * Fetches a single book by ID from the backend
 */
export const fetchBookById = async (id) => {
    try {
        const response = await fetch(`${API_URL}/journals/${id}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Book with ID ${id} not found`);
            }
            throw new Error(`Failed to fetch book: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error fetching book ${id}:`, error);
        throw error;
    }
};

/**
 * Fetches a single simplified book by ID from the backend
 */
export const fetchSimplifiedBookById = async (id) => {
    try {
        const response = await fetch(`${API_URL}/journals/simplified/${id}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Book with ID ${id} not found`);
            }
            throw new Error(`Failed to fetch simplified book: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error fetching simplified book ${id}:`, error);
        throw error;
    }
};

/**
 * Creates a new book on the backend
 */
export const createBook = async (bookData) => {
    try {
        const response = await fetch(`${API_URL}/journals`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bookData),
        });

        if (!response.ok) {
            throw new Error(`Failed to create book: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error('Error creating book:', error);
        throw error;
    }
};

/**
 * Updates an existing book on the backend
 */
export const updateBook = async (id, bookData) => {
    try {
        const response = await fetch(`${API_URL}/journals/${id}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bookData),
        });

        if (!response.ok) {
            throw new Error(`Failed to update book: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error updating book ${id}:`, error);
        throw error;
    }
};

/**
 * Deletes a book from the backend
 */
export const deleteBook = async (id) => {
    try {
        const response = await fetch(`${API_URL}/journals/${id}`, {
            method: 'DELETE',
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to delete book: ${response.status} ${response.statusText}`);
        }
    } catch (error) {
        console.error(`Error deleting book ${id}:`, error);
        throw error;
    }
};

/**
 * Searches for books with advanced filters
 */
export const searchBooks = async (filters = {}, page = 0, size = 12) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString()
        });

        if (filters.title) params.append('title', filters.title);
        if (filters.authorName) params.append('authorName', filters.authorName);
        if (filters.bookType) params.append('bookType', filters.bookType);
        if (filters.publicationYear) params.append('publicationYear', filters.publicationYear);
        if (filters.countryOfOrigin) params.append('countryOfOrigin', filters.countryOfOrigin);

        const response = await fetch(`${API_URL}/journals/search?${params}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to search books: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error('Error searching books:', error);
        throw error;
    }
};

/**
 * Fetches available genres from the backend
 */
export const fetchGenres = async () => {
    try {
        const response = await fetch(`${API_URL}/journals/genres`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch genres: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error('Error fetching genres:', error);
        throw error;
    }
};

/**
 * Fetches all books by a specific author
 */
export const fetchBooksByAuthor = async (authorId) => {
    try {
        const response = await fetch(`${API_URL}/journals/by-author/${authorId}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch books by author: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error fetching books by author ${authorId}:`, error);
        throw error;
    }
};

/**
 * Fetches simplified book data for all books by a specific author
 */
export const fetchSimplifiedBooksByAuthor = async (authorId) => {
    try {
        const response = await fetch(`${API_URL}/journals/simplified/by-author/${authorId}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch simplified books by author: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error fetching simplified books by author ${authorId}:`, error);
        throw error;
    }
};
